import React, { useState } from "react";
import { auth, db } from "../../firebase";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import Window, { Footer } from "../../components/Window";
import Icon from "../../components/Icon";
import { SENDER } from "../../components/AccountNotices";
import { isReservedUsername } from "../../welcome";
import "../../styles.css";

const Signup = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAdult, setIsAdult] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false); // Added loading state
  const navigate = useNavigate();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!isAdult) {
      setError("Please confirm you're 18 or older and agree to the guidelines.");
      setLoading(false);
      return;
    }

    const handle = username.toLowerCase().trim();
    if (!/^[a-z0-9_.-]{3,24}$/.test(handle)) {
      setError(
        "Usernames are 3–24 characters: letters, numbers, dots, dashes, or underscores."
      );
      setLoading(false);
      return;
    }
    if (isReservedUsername(handle)) {
      setError("That username is saved for the PenPal Board team. Try another?");
      setLoading(false);
      return;
    }

    try {
      // 1. Create the Auth User. (Only signed-in members can look up other
      //    members, so the username check happens right after this.)
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // 2. Make sure nobody already has that username; if so, undo the signup.
      const snapshot = await getDocs(
        query(collection(db, "users"), where("username", "==", handle))
      );
      if (!snapshot.empty) {
        await deleteUser(userCredential.user);
        setError("That username is already taken.");
        setLoading(false);
        return;
      }

      // 3. Update Auth Profile
      await updateProfile(userCredential.user, {
        displayName: username.trim(),
      });

      // 4. Create Firestore Document. Email is not stored here, because
      //    other members can read profiles; Firebase Auth already keeps it.
      const uid = userCredential.user.uid;
      await setDoc(doc(db, "users", uid), {
        uid,
        username: handle,
        name: "",
        pronouns: "",
        location: "",
        preferences: "",
        profileRequests: "",
        requests: "",
        topics: "",
        hobbies: "",
        goals: "",
        visibility: {
          age: false,
          location: false,
          name: false,
          pronouns: false,
          showOnBoard: true,
        },
        createdAt: new Date().toISOString(),
        // when they confirmed 18+ and agreed to the guidelines and privacy page
        agreedAt: new Date().toISOString(),
      });

      // 5. Ask them to verify their email (needed before posting or writing).
      await sendEmailVerification(userCredential.user).catch((err) =>
        console.error("Verification email failed:", err)
      );

      navigate("/dashboard");
    } catch (err: any) {
      console.error("Signup Error:", err);
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="mb-5 text-center">
          <span className="logo">
            <Icon name="mail" size={37} tone="pink" pop className="mr-1" />
            PenPal Board
            <span className="logo-sub">the 2SLGBTQIA+ pen pal network</span>
          </span>
        </div>

        <Window title="new_member.exe" tone="blue" minimizable={false}>
          <h1 className="window-heading">
            Join the network <Icon name="heart" tone="pink" size={26} pop />
          </h1>
          <p className="window-sub">
            Find pals who get it. Slow letters, real friendships.
          </p>

          <form onSubmit={handleSignup} className="flex flex-col gap-4">
            <label className="field">
              <span className="field-label">Username</span>
              <input
                type="text"
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label className="field">
              <span className="field-label">Email</span>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label className="field">
              <span className="field-label">Password</span>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            <label className="check notice">
              <input
                type="checkbox"
                checked={isAdult}
                onChange={(e) => setIsAdult(e.target.checked)}
                required
              />
              <span className="text-sm">
                <strong>I'm 18 or older</strong>, and I agree to the{" "}
                <Link to="/guidelines" target="_blank">
                  community guidelines
                </Link>{" "}
                and have read the{" "}
                <Link to="/privacy" target="_blank">
                  privacy page
                </Link>
                .
              </span>
            </label>

            <p className="text-xs text-ink-soft">
              After you join, we'll email you a link to verify your address,
              from <strong>{SENDER}</strong>. It often lands in spam, so check
              there too.
            </p>

            {error && <p className="error-text">{error}</p>}

            <button type="submit" disabled={loading} className="btn w-full">
              {loading ? "Creating your account..." : "Sign up ✿"}
            </button>
          </form>

          <div className="divider my-4" />

          <p className="text-center text-sm">
            Already a member? <Link to="/signin">Sign in</Link>
          </p>
        </Window>

        <Footer />
      </div>
    </div>
  );
};

export default Signup;
